import { Empty, Flex, Spin, Table, Tag } from 'antd';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import Arrow from '@/assets/icons/chevron-right.svg?react';
import ArrowLeft from '@/assets/icons/chevron-left.svg?react';
import { IncomingNumber } from '@/services/phone-numbers-service/phone-numbers-service.types';
import { calculateDateFromISOtoYYYY_MM_DD, calculateHourFromISODate } from '@/utils/helpers';
import styles from './incoming-numbers-table.module.scss';
import { useTranslation } from 'react-i18next';

interface IncomingNumbersTableProps {
  numbers?: IncomingNumber[];
  loading: boolean;
}

// Titles are looked up at render time (`t()` at module level would freeze the language at import).
const useIncomingNumbersColumns = (): ColumnsType<IncomingNumber> => {
  const { t } = useTranslation(['phoneNumbers', 'common']);

  return [
    {
      title: t('phoneNumbers:columns.phoneNumber'),
      dataIndex: 'phone_number',
      key: 'phone_number',
      className: styles.phoneNumberContainer,
    },
    {
      title: t('phoneNumbers:columns.friendlyName'),
      dataIndex: 'friendly_name',
      key: 'friendly_name',
      className: styles.friendlyNameContainer,
    },
    {
      title: t('phoneNumbers:columns.voiceUrl'),
      dataIndex: 'voice_url',
      key: 'voice_url',
      className: styles.voiceUrlContainer,
      render: (url: string | null) => url || '-',
    },
    {
      title: t('phoneNumbers:columns.smsUrl'),
      dataIndex: 'sms_url',
      key: 'sms_url',
      className: styles.smsUrlContainer,
      render: (url: string | null) => url || '-',
    },
    {
      title: t('phoneNumbers:columns.assignedInCrm'),
      dataIndex: 'assigned_in_crm',
      key: 'assigned_in_crm',
      className: styles.assignedContainer,
      render: (assigned: boolean) => (
        <Tag color={assigned ? 'green' : 'default'}>
          {assigned ? t('common:states.yes') : t('common:states.no')}
        </Tag>
      ),
    },
    {
      title: t('phoneNumbers:columns.dateCreated'),
      dataIndex: 'date_created',
      key: 'date_created',
      className: styles.dateContainer,
      render: (date: string) => (
        <Flex wrap="wrap" align="center">
          <p className={styles.date}>{calculateDateFromISOtoYYYY_MM_DD(date)}</p>
          <span className={styles.circle}>&#9679;</span>
          <p className={styles.hour}>{calculateHourFromISODate(date)}</p>
        </Flex>
      ),
    },
  ];
};

export function IncomingNumbersTable({ numbers, loading }: IncomingNumbersTableProps) {
  const columns = useIncomingNumbersColumns();

  return (
    <Table
      columns={columns}
      dataSource={numbers}
      rowKey="sid"
      scroll={{ x: 800 }}
      className={styles.table}
      pagination={{
        position: ['bottomCenter'],
        pageSizeOptions: ['12', '25', '50'],
        showSizeChanger: true,
        defaultPageSize: 12,
        locale: { items_per_page: '' },
        nextIcon: <Icon component={() => <Arrow strokeWidth={1} width={16} height={16} />} />,
        prevIcon: <Icon component={() => <ArrowLeft strokeWidth={2} width={16} height={16} />} />,
      }}
      locale={{
        emptyText: loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <Spin indicator={<LoadingOutlined spin />} />
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ),
      }}
    />
  );
}